import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { cn } from '@/lib/cn';
import type { ClientOption } from '@/features/incoming/types';
import { operationInputClass } from './OperationField';

export interface ClientPickerProps {
  clients: ClientOption[];
  /** The chosen client, resolved even when the current search excludes it. */
  selected: ClientOption | null;
  onChange: (clientId: number | undefined) => void;
  onSearchChange: (search: string) => void;
  isLoading: boolean;
}

/**
 * Picks a client by typing (#271).
 *
 * A plain dropdown can only offer what was loaded, and one request brings
 * back 500 clients. The search goes to the server, so a tenant's 900th
 * client is two keystrokes away rather than unreachable.
 */
export function ClientPicker({
  clients,
  selected,
  onChange,
  onSearchChange,
  isLoading,
}: ClientPickerProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => onSearchChange(search), 300);
    return () => clearTimeout(timer);
  }, [search, onSearchChange]);

  useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    },
    [],
  );

  const options = useMemo(() => clients.slice(0, 50), [clients]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search, isOpen]);

  const choose = (client: ClientOption) => {
    onChange(client.id);
    setSearch('');
    setIsOpen(false);
  };

  // Un operateur au clavier pouvait taper sa recherche et rester devant une
  // liste qu'aucune touche n'atteignait. Fleches pour parcourir, Entree
  // pour prendre, Echap pour refermer sans rien changer.
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      if (options.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => (i + step + options.length) % options.length);
      return;
    }
    if (event.key === 'Enter') {
      if (!isOpen) return;
      const picked = options[activeIndex];
      if (picked) {
        event.preventDefault();
        choose(picked);
      }
      return;
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <div className="relative max-w-md">
      <input
        id="operation-client"
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="operation-client-listbox"
        aria-autocomplete="list"
        aria-label="Client"
        value={isOpen ? search : (selected?.label ?? '')}
        placeholder={isLoading ? 'Loading clients…' : 'Search clients…'}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={onKeyDown}
        aria-activedescendant={
          isOpen && options[activeIndex]
            ? `operation-client-option-${options[activeIndex].id}`
            : undefined
        }
        onFocus={() => {
          setIsOpen(true);
          setSearch('');
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setIsOpen(false), 200);
        }}
        className={cn(operationInputClass, 'border-[#e0e0e0]')}
        data-testid="operation-client"
      />

      {isOpen && options.length > 0 && (
        <ul
          id="operation-client-listbox"
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-[#e0e0e0] bg-white shadow-lg"
          data-testid="operation-client-listbox"
        >
          {options.map((client, index) => (
            <li
              key={client.id}
              id={`operation-client-option-${client.id}`}
              role="option"
              aria-selected={client.id === selected?.id}
              className={cn(
                'cursor-pointer px-4 py-2 text-sm hover:bg-[#f5f5f5]',
                index === activeIndex && 'bg-[#f5f5f5]',
                client.id === selected?.id && 'bg-[#fafafa] font-medium',
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(client);
              }}
            >
              {client.label}
            </li>
          ))}
        </ul>
      )}

      {isOpen && options.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-[#e0e0e0] bg-white px-4 py-3 text-sm text-[#999]">
          {isLoading ? 'Searching…' : 'No clients found'}
        </div>
      )}
    </div>
  );
}
